import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, Stack } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import CustomPagination from '../../../components/customPagination';
import { useGetPendingSignupsHandler, useApproveSignupHandler } from '../dataHandlers/signupRequestsHandler';
import { usePermissions } from '../../../permissions/usePermissions';
import ApproveSignupModal from './ApproveSignupModal';

const PendingSignupsTable = ({
  handlePaginationChange,
  searchQuery = {},
  paginationModel = { page: 0, pageSize: 10 },
}) => {
  const { pendingSignups, totalDocuments, isLoading } = useGetPendingSignupsHandler({
    searchQuery,
    enabled: true,
  });
  const { canCreate } = usePermissions();
  const [selectedSignup, setSelectedSignup] = useState(null);
  const { onApprove, isPending, approvingOrgId } = useApproveSignupHandler({
    onSuccess: () => setSelectedSignup(null),
  });

  const rows = pendingSignups.filter((row) => row?.email || row?.org_id);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(totalDocuments / paginationModel.pageSize) - 1);
    if (paginationModel.page > maxPage) {
      handlePaginationChange(0, paginationModel.pageSize);
    }
  }, [totalDocuments, paginationModel.page, paginationModel.pageSize, handlePaginationChange]);

  const columns = [
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
    },
    {
      field: 'name',
      headerName: 'Contact Name',
      flex: 0.75,
    },
    {
      field: 'organization_name',
      headerName: 'Organization',
      flex: 0.75,
    },
    {
      field: 'contact_email',
      headerName: 'Contact email',
      flex: 0.75,
      valueFormatter: (value) => value || '—',
    },
    {
      field: 'submitted_at',
      headerName: 'Submitted',
      flex: 0.75,
      valueFormatter: (value) => {
        if (!value) return '—';
        return new Date(value).toLocaleString();
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      sortable: false,
      renderCell: (params) => {
        const isApproving = isPending && approvingOrgId === params.row.org_id;

        return (
          <Button
            variant="contained"
            size="small"
            disabled={!canCreate('organizations') || isPending}
            onClick={() => setSelectedSignup(params.row)}
          >
            {isApproving ? 'Approving…' : 'Approve'}
          </Button>
        );
      },
    },
  ];

  return (
    <Box>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.org_id || row.user_id || row.email}
        getRowHeight={() => 'auto'}
        paginationMode="server"
        sx={{
          '& .MuiDataGrid-cell': {
            alignItems: 'center',
          },
        }}
        rowCount={totalDocuments}
        loading={isLoading}
        paginationModel={paginationModel}
        pageSizeOptions={[10, 25]}
        disableColumnMenu
        disableColumnSorting
        disableColumnFilter
        disableRowSelectionOnClick
        onPaginationModelChange={(model) => {
          handlePaginationChange(model.page, model.pageSize);
        }}
        slots={{
          pagination: CustomPagination,
        }}
      />

      <ApproveSignupModal
        open={Boolean(selectedSignup)}
        signup={selectedSignup}
        onClose={() => setSelectedSignup(null)}
        onConfirm={() => selectedSignup && onApprove(selectedSignup.org_id)}
        isLoading={isPending && approvingOrgId === selectedSignup?.org_id}
      />
    </Box>
  );
};

export default PendingSignupsTable;
